import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { SmartPanelManagerService, PanelPreset } from '../services/smart-panel-manager.service';
import { BuilderComponentType } from '../interfaces/builder-screen.models';
import { Observable } from 'rxjs';
import { distinctUntilChanged, tap } from 'rxjs/operators';

@Component({
  selector: 'app-panel-preset-selector',
  templateUrl: './panel-preset-selector.component.html',
  styleUrls: ['./panel-preset-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PanelPresetSelectorComponent implements OnInit, OnChanges {
  @Input() componentType: BuilderComponentType | null = null;
  @Input() compact = false;
  @Output() presetSelected = new EventEmitter<string>();

  presets: PanelPreset[] = [];
  currentPreset$: Observable<string | null>;
  
  // Gerenciamento de estado do preset ativo (equivalente ao useState)
  activePreset: string = 'standard';
  
  // Define presets com mapeamento de painéis visíveis
  private presetConfig = {
    minimal: { visiblePanels: ['essential'] },
    standard: { visiblePanels: ['essential', 'appearance', 'layout'] },
    advanced: { visiblePanels: ['essential', 'appearance', 'layout', 'database', 'validation', 'advanced'] }
  };

  constructor(
    private smartPanelManager: SmartPanelManagerService,
    private cdr: ChangeDetectorRef
  ) {
    this.currentPreset$ = this.smartPanelManager.currentPreset$.pipe(
      // CORREÇÃO CRÍTICA: Adiciona operadores RxJS adequados
      distinctUntilChanged(),
      tap(presetId => console.log(`📋 PanelPresetSelector: Received preset change: "${presetId}"`))
    );
    
    // Subscreve a mudanças de preset para manter estado local sincronizado
    this.currentPreset$.subscribe(presetId => {
      if (presetId && presetId !== this.activePreset) {
        console.log(`📋 Syncing active preset from ${this.activePreset} to ${presetId}`);
        this.activePreset = presetId;
        
        // CORREÇÃO CRÍTICA: Força detecção de mudanças com timing adequado
        this.cdr.markForCheck();
        setTimeout(() => {
          this.cdr.detectChanges();
        }, 0);
      }
    });
  }

  ngOnInit() {
    // Inicializa preset ativo
    this.initializeActivePreset();
    
    // Presets simplificados - apenas 3 opções claras
    this.presets = [
      {
        id: 'minimal',
        name: 'builderScreen.presets.minimal.name',
        description: 'builderScreen.presets.minimal.description',
        icon: 'minus-circle',
        panels: {
          essential: true,
          appearance: false,
          layout: false,
          database: false,
          validation: false,
          advanced: false
        }
      },
      {
        id: 'standard',
        name: 'builderScreen.presets.standard.name',
        description: 'builderScreen.presets.standard.description',
        icon: 'check-circle',
        panels: {
          essential: true,
          appearance: true,
          layout: true,
          database: false,
          validation: false,
          advanced: false
        }
      },
      {
        id: 'advanced',
        name: 'builderScreen.presets.advanced.name',
        description: 'builderScreen.presets.advanced.description',
        icon: 'plus-circle',
        panels: {
          essential: true,
          appearance: true,
          layout: true,
          database: true,
          validation: true,
          advanced: true
        }
      }
    ];
  }

  ngOnChanges(changes: SimpleChanges) {
    // Re-inicializa quando componentType muda
    if (changes['componentType'] && !changes['componentType'].firstChange) {
      const previousType = changes['componentType'].previousValue;
      const currentType = changes['componentType'].currentValue;
      
      console.log(`🔄 COMPONENT TYPE CHANGED in preset selector: ${previousType} → ${currentType}`);
      
      // CORREÇÃO CRÍTICA: Reseta estado do painel de preset para novo componente
      this.resetPresetPanelForNewComponent(currentType);
      
      // Re-inicializa preset ativo para novo componente
      this.initializeActivePreset();
    }
  }

  private initializeActivePreset() {
    // Obtém preset atual do gerenciador de painéis inteligente ou usa padrão
    const currentPreset = this.smartPanelManager.getCurrentPreset();
    const initialPreset = currentPreset || 'standard';
    
    console.log(`🚀 Initializing active preset: ${initialPreset} (from smart manager: ${currentPreset})`);
    
    // Atualiza tanto estado local quanto gerenciador de painéis inteligente para garantir sincronização
    this.activePreset = initialPreset;
    
    // Se o gerenciador de painéis inteligente não tem preset, define-o
    if (!currentPreset) {
      this.smartPanelManager.setCurrentPreset(initialPreset);
    }
    
    // Force initial change detection
    this.cdr.markForCheck();
    this.cdr.detectChanges();
    
    console.log(`✅ Active preset initialized: ${this.activePreset}`);
  }

  onPresetSelect(presetId: string, event?: Event) {
    console.log(`🖱️ PRESET CARD CLICKED: ${presetId}`);
    console.log(`📋 Event type: ${event?.type}`);
    console.log(`📋 Component type: ${this.componentType}`);
    
    // Prevent event bubbling and default behavior
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // TEMPORARY FIX: Allow preset selection even without componentType
    if (!this.componentType) {
      console.warn(`⚠️ No componentType set, but proceeding with preset selection anyway`);
      // Don't return - continue with preset selection
    }
    
    console.log(`✅ Calling handlePresetClick for: ${presetId}`);
    
    // INSTANT FUNCTIONALITY FIX: Apply preset immediately
    this.handlePresetClick(presetId);
  }

  private handlePresetClick(presetKey: string) {
    console.log(`🔥 HANDLE PRESET CLICK CALLED: ${presetKey}`);
    console.log(`📋 Component type: ${this.componentType}`);
    console.log(`📋 Current activePreset: ${this.activePreset}`);
    
    // TEMPORARY FIX: Allow preset handling even without componentType
    if (!this.componentType) {
      console.warn(`⚠️ No componentType set, but proceeding with preset handling anyway`);
      // Don't return - continue with preset handling
    }

    // Prevent duplicate clicks on same preset (temporarily disabled for debugging)
    if (this.activePreset === presetKey) {
      console.log(`⚠️ Preset "${presetKey}" already active, but processing anyway for debugging`);
      // return; // Commented out to allow re-clicking same preset
    }

    console.log(`🎯 Processing preset change from "${this.activePreset}" to "${presetKey}"`);
    console.log(`📋 Previous activePreset: ${this.activePreset}`);

    // 1. Update active preset state immediately (useState equivalent)
    this.activePreset = presetKey;
    console.log(`✅ Updated activePreset to: ${this.activePreset}`);
    
    // 2. Get visible panels for this preset
    const presetConfig = this.presetConfig[presetKey as keyof typeof this.presetConfig];
    if (!presetConfig) {
      console.error(`❌ Unknown preset: ${presetKey}`);
      return;
    }
    
    // CRITICAL FIX: Apply changes in the correct order with proper timing
    
    // 3. Update smart panel manager FIRST
    this.smartPanelManager.setCurrentPreset(presetKey);
    
    // 4. Apply preset through smart panel manager
    setTimeout(() => {
      if (this.componentType) {
        this.smartPanelManager.applyPreset(this.componentType, presetKey);
      } else {
        console.warn(`⚠️ Skipping applyPreset - no componentType`);
      }
    }, 10);
    
    // 5. Update inspector panels
    setTimeout(() => {
      this.setInspectorPanels(presetConfig.visiblePanels);
    }, 20);
    
    // 6. Emit event to parent component
    setTimeout(() => {
      this.presetSelected.emit(presetKey);
    }, 30);
    
    // 7. Force immediate visual feedback and UI updates
    setTimeout(() => {
      this.forceUIUpdate(presetKey);
    }, 40);
    
    console.log(`⚡ Preset "${presetKey}" applied instantly with panels:`, presetConfig.visiblePanels);
  }

  private forceUIUpdate(presetKey: string) {
    // 1. Force change detection immediately
    this.cdr.markForCheck();
    this.cdr.detectChanges();
    
    // 2. Update button highlighting
    this.updateButtonHighlighting();
    
    // 3. Show visual feedback
    this.showInstantFeedback(presetKey);
    
    // 4. Additional change detection cycle to ensure everything updates
    setTimeout(() => {
      this.cdr.markForCheck();
    }, 0);
  }

  private updateButtonHighlighting() {
    console.log(`🎨 Updating button highlighting for activePreset: ${this.activePreset}`);
    
    // Force change detection to update Angular bindings
    this.cdr.markForCheck();
    this.cdr.detectChanges();
    
    // Verify DOM state after change detection
    setTimeout(() => {
      const allCards = document.querySelectorAll('.preset-card');
      console.log(`🔍 Found ${allCards.length} preset cards`);
      
      allCards.forEach((card, index) => {
        const presetId = card.getAttribute('data-preset');
        const isActive = presetId === this.activePreset;
        const hasSelectedClass = card.classList.contains('selected');
        
        console.log(`📋 Card ${index + 1}: preset="${presetId}", active=${isActive}, hasSelectedClass=${hasSelectedClass}`);
        
        // Ensure DOM state matches activePreset
        if (isActive && !hasSelectedClass) {
          card.classList.add('selected');
          console.log(`✅ Added 'selected' class to ${presetId}`);
        } else if (!isActive && hasSelectedClass) {
          card.classList.remove('selected');
          console.log(`❌ Removed 'selected' class from ${presetId}`);
        }
      });
    }, 10);
  }

  private setInspectorPanels(visiblePanels: string[]) {
    console.log(`🔄 Setting inspector panels:`, visiblePanels);
    console.log(`📋 Component type available: ${!!this.componentType}`);
    
    // Update smart panel manager with visible panels
    if (this.componentType) {
      // Set all panels to collapsed first
      const allPanels = ['essential', 'appearance', 'layout', 'database', 'validation', 'advanced'];
      
      allPanels.forEach(panelId => {
        const shouldBeVisible = visiblePanels.includes(panelId);
        this.smartPanelManager.updatePanelState(this.componentType!, panelId, shouldBeVisible);
      });
      
      console.log(`✅ Inspector panels updated:`, visiblePanels);
    } else {
      console.warn(`⚠️ Cannot update inspector panels - no componentType set`);
    }
  }

  private showInstantFeedback(presetId: string) {
    console.log(`🎨 Showing instant feedback for preset: ${presetId}`);
    
    // Add visual feedback class to the selected card
    const selectedCard = document.querySelector(`.preset-card[data-preset="${presetId}"]`);
    if (selectedCard) {
      selectedCard.classList.add('instant-applied');
      console.log(`✨ Added instant-applied class to preset card: ${presetId}`);
      
      setTimeout(() => {
        selectedCard.classList.remove('instant-applied');
        console.log(`🔄 Removed instant-applied class from preset card: ${presetId}`);
      }, 600);
    } else {
      console.warn(`⚠️ Could not find preset card for: ${presetId}`);
    }
  }

  getPresetTooltip(preset: PanelPreset): string {
    // Return the specific tooltip translation key for each preset
    return `builderScreen.presets.${preset.id}.tooltip`;
  }

  /**
   * CRITICAL FIX: Reset preset panel state when switching to a new component
   * This prevents state persistence between different components
   */
  private resetPresetPanelForNewComponent(newComponentType: string | null) {
    console.log(`🔄 RESETTING PRESET PANEL for new component type: ${newComponentType}`);
    
    // 1. Reset active preset to default for new component type
    const defaultPreset = this.getDefaultPresetForComponentType(newComponentType);
    this.activePreset = defaultPreset;
    
    // 2. Update smart panel manager with new default
    this.smartPanelManager.setCurrentPreset(defaultPreset);
    
    // 3. Clear any temporary visual states
    this.clearTemporaryVisualStates();
    
    // 4. Force UI refresh
    this.cdr.markForCheck();
    this.cdr.detectChanges();
    
    console.log(`✅ Preset panel reset complete - new active preset: ${defaultPreset}`);
  }
  
  /**
   * Get default preset for specific component type
   */
  private getDefaultPresetForComponentType(componentType: string | null): string {
    if (!componentType) return 'standard';
    
    // Define default presets based on component complexity
    const complexComponents = ['table', 'simpletable', 'tabs'];
    const basicComponents = ['text', 'textarea', 'number', 'switch', 'button'];
    
    if (basicComponents.includes(componentType)) {
      return 'minimal'; // Basic components start with minimal preset
    } else if (complexComponents.includes(componentType)) {
      return 'advanced'; // Complex components start with advanced preset
    } else {
      return 'standard'; // Default for unknown components
    }
  }
  
  /**
   * Clear temporary visual states
   */
  private clearTemporaryVisualStates() {
    console.log(`🎨 Clearing temporary visual states`);
    
    // Remove any temporary CSS classes that might persist
    const allCards = document.querySelectorAll('.preset-card');
    allCards.forEach(card => {
      card.classList.remove('active', 'processing', 'temporary-highlight');
    });
    
    // Force button highlighting update
    setTimeout(() => {
      this.updateButtonHighlighting();
    }, 10);
  }

  // Debug method to check current state
  debugCurrentState() {
    console.log(`🐛 DEBUG: Current component state:`);
    console.log(`  - componentType: ${this.componentType}`);
    console.log(`  - activePreset: ${this.activePreset}`);
    console.log(`  - presets.length: ${this.presets.length}`);
    console.log(`  - smartPanelManager.getCurrentPreset(): ${this.smartPanelManager.getCurrentPreset()}`);
    
    // Check DOM state
    const cards = document.querySelectorAll('.preset-card');
    console.log(`  - DOM cards found: ${cards.length}`);
    cards.forEach((card, index) => {
      const presetId = card.getAttribute('data-preset');
      const hasSelected = card.classList.contains('selected');
      console.log(`    ${index + 1}. ${presetId}: selected=${hasSelected}`);
    });
  }

  // Direct test method for preset functionality
  testPresetDirect(presetId: string) {
    console.log(`🧪 DIRECT PRESET TEST: ${presetId}`);
    this.handlePresetClick(presetId);
  }

  getActivePresetName(): string {
    if (!this.activePreset) return 'builderScreen.presets.standard.name';
    
    const preset = this.presets.find(p => p.id === this.activePreset);
    return preset?.name || 'builderScreen.presets.standard.name';
  }

  getPresetIcon(presetId: string): string {
    // Return appropriate icons for each preset type
    const iconMap: { [key: string]: string } = {
      'minimal': 'sliders',           // SlidersHorizontal equivalent
      'standard': 'setting',          // Settings equivalent  
      'advanced': 'control'           // PanelRight equivalent
    };
    
    return iconMap[presetId] || 'setting';
  }
}
